// The words on the public landing page.
//
// Copy lives here rather than inline in the JSX for two reasons: a test can
// assert on it (see Home.test.tsx, which checks we never ship a placeholder or
// an unsubstantiated claim), and a non-developer editing the pitch does not
// have to read React.
//
// One rule, enforced by test: nothing on this page may claim social proof we
// do not have. No customer counts, no logos, no testimonials, no "trusted by
// thousands". Every number below is either our own published price or a fact
// about how the product works. A fabricated testimonial on a page selling to
// businesses is not a growth tactic, it is a lie with our address in the
// footer.

import { CUSTOM_WORK_OPEN, CUSTOM_WORK_VISIBLE } from "./devTime";
import { EARLY_ACCESS, ENTERPRISE, FREE_ANSWERS_PER_MONTH, PLANS, answers, earlyAccessActive, usd } from "./plans";

export type Step = { title: string; body: string };
export type Feature = { title: string; body: string };
export type Faq = { q: string; a: string };

export const HERO_HEADLINE = "Your business should answer for itself.";

// Short on purpose (Luis, October 24, 2026: "way too wordy"). On a phone the
// old 55 words filled the whole first screen; the details are further down.
export const HERO_SUBHEAD =
  "Turn your website and FAQs into an assistant that answers your customers " +
  "instantly, on your site or from a link or QR code. No code, live in minutes.";

// The badge above the headline.
export const HERO_BADGE = "Live in minutes, everywhere customers find you";

// Two doors out of the hero, deliberately.
//
// Some people will not type a word into a product that has not told them whose
// it is; others will not hand over an email address to something they have not
// seen work. Forcing everyone down one of those routes loses whichever half we
// guessed wrong about, and the wizard runs signed out anyway, so the page offers
// both and means it.
//
// The primary goes to /start, which is the wizard with no account attached. The
// secondary goes to the sign-up form, for the people who would rather have the
// account settled before they start.
export const HERO_PRIMARY_CTA = "Build your bot free";
export const HERO_SECONDARY_CTA = "Create an account first";

// Printed under the two CTAs. It exists because "no account needed" raises an
// obvious worry -- that the work will evaporate at the signup wall -- and the
// answer is that it does not, so we should say so where the worry occurs.
export const HERO_PATH_NOTE = "No account needed to start, and nothing you type is lost when you sign up.";

export const HOW_IT_WORKS_CTA = "See how it works";

// Sits under the hero CTAs where a "trusted by" logo strip would normally go.
// These are commitments we actually keep, which is why they can be printed.
export const HERO_ASSURANCES = [
  "No credit card to start",
  "No code to write",
  "Cancel anytime"
];

export const HOW_IT_WORKS_BODY =
  "You can go from nothing to a working assistant, on your website and on your business " +
  "cards, in a single sitting.";

export const STEPS: Step[] = [
  {
    title: "Give it your content",
    body:
      "Upload your FAQ or help docs, paste in questions and answers, or point it at " +
      "the pages on your site. It reads all of it and learns to answer from it."
  },
  {
    title: "Make it yours",
    body:
      "Pick your colors, your greeting, and how the chat looks. It should feel like " +
      "part of your business, not like somebody else's product."
  },
  {
    title: "Put it everywhere",
    body:
      "Paste one line into your website, print its QR code on your business cards and " +
      "shop window, and add its link to your email signature and LinkedIn. No plugin, " +
      "no developer."
  }
];

export const FEATURES: Feature[] = [
  {
    title: "Answers from your content, not guesses",
    body:
      "It replies using the material you gave it. That is the whole point: a support " +
      "bot inventing an answer about your refund policy is worse than no bot at all."
  },
  {
    title: "Books appointments while you sleep",
    body:
      "On a paid plan, connect Cal.com or Calendly and the assistant can offer your real " +
      "open times and get the meeting on your calendar, in the middle of the same conversation."
  },
  {
    title: "Suggested questions",
    body:
      "Give visitors up to four one-tap questions to start with, so nobody stares at an " +
      "empty box. They step aside once the conversation gets going, or stay if you prefer."
  },
  {
    title: "Looks like your brand",
    body:
      "Your colors, your wording, your placement. And it follows your visitor's " +
      "light or dark setting so it never glares at somebody reading at night."
  },
  {
    title: "Everywhere your customers find you",
    body:
      "One line of HTML on any website (Shopify, Squarespace, WordPress, Webflow). A " +
      "short link for your email signature, LinkedIn and Instagram. A QR code for " +
      "business cards, menus, flyers and shop windows."
  },
  {
    title: "More than one assistant",
    body:
      "Run a different bot for each product, brand or client, each with its own " +
      "content and its own look, all from one account."
  },
  {
    title: "You decide the limits",
    body:
      "Cap how many questions one visitor can ask per conversation, and set a monthly " +
      "spending limit on extra answers. A busy month is never a surprise bill."
  }
];

export const FAQS: Faq[] = [
  {
    q: "Do I need a developer?",
    a:
      "No. Setting the assistant up is a form, and installing it is pasting one line " +
      "into your site. If you would rather hand that line to whoever maintains your " +
      "site, that works too."
  },
  {
    q: "I don't have a website. Can I still use it?",
    a:
      "Yes. Every assistant has its own page at a short link, like " +
      "buildbotsnow.com/c/your-business, and a QR code that opens it. Put them on " +
      "business cards, flyers, menus, your shop window, your email signature, " +
      "LinkedIn or Instagram, and people get answers on their phone."
  },
  {
    q: "Where do the answers come from?",
    a:
      "From the content you give it: your documents, your written answers, and the " +
      "pages you point it at. It answers from your material rather than from general " +
      "knowledge about your industry."
  },
  {
    q: "What happens when it does not know?",
    a:
      "It says so, rather than making something up. You can add the missing answer " +
      "and it picks it up on the next update."
  },
  {
    q: "Can I try it before paying?",
    a:
      "Yes. Build an assistant, put it on your site or share its link, and it answers " +
      `up to ${FREE_ANSWERS_PER_MONTH} questions a month at no cost, from any number of visitors. Plans ` +
      `start at ${usd(PLANS.starter.priceUsd)} a month for ${answers(PLANS.starter.includedAnswers)} answers when you need more.`
  },
  // Only while it is true (lib/plans.ts EARLY_ACCESS).
  ...(earlyAccessActive()
    ? [
        {
          q: "Will the price go up?",
          a:
            `For new subscriptions, yes: ${PLANS.starter.name} is ${usd(EARLY_ACCESS.priceUsd)} a month while we are in early access, ` +
            `and ${usd(EARLY_ACCESS.laterPriceUsd)} from ${EARLY_ACCESS.untilLabel}. Subscribe before then and you keep ` +
            `${usd(EARLY_ACCESS.priceUsd)} for as long as you stay on ${PLANS.starter.name}.`
        }
      ]
    : []),
  {
    q: "What if my assistant gets busier than my plan?",
    a:
      "It keeps answering. Extra answers are billed per 1,000 on your next invoice, " +
      "up to a monthly spending limit you set, and you can move to a bigger plan at " +
      `any time. For very busy sites, Enterprise plans start at ${usd(ENTERPRISE.fromUsd)} a month.`
  },
  {
    q: "Can it connect to the tools I already use?",
    // Follows CUSTOM_WORK_VISIBLE and CUSTOM_WORK_OPEN: says nothing about
    // custom work while it is hidden, and never promises a quote nobody is
    // ready to give.
    a:
      "Every plan hands visitors to your team by email. Paid plans also book meetings " +
      "through Cal.com and Calendly, hand visitors over in Slack, Discord, a webhook or live " +
      "chat, and look things up in your own API, like an order's status or what is in stock, " +
      "from its OpenAPI description." +
      (!CUSTOM_WORK_VISIBLE
        ? ""
        : CUSTOM_WORK_OPEN
          ? " Anything beyond that we quote as development work: you are buying our " +
            "time to build the specific integration you need, not a subscription tier."
          : " We are not taking custom integration work, like connecting your CRM or " +
            "ticketing system, yet.")
  },
  {
    q: "Can my team use it too?",
    a:
      "Yes, at no extra cost. Invite people by email as an Owner, Admin, Member or " +
      "Reporting user: Members keep the assistants' knowledge up to date and answer " +
      "customers in live chat, Admins also change their settings, integrations and the " +
      "team, and Reporting users can see " +
      "conversations, usage and invoices without changing anything. You can also set " +
      "a billing email, so invoices go to whoever pays them."
  },
  {
    q: "Who is behind this?",
    a:
      "Build Bots Now is a Nebulon Labs product. Our company name and postal " +
      "address are in our Terms of Use and Privacy Policy, which is more than you " +
      "can say for a lot of software."
  }
];

// The pricing section's framing. Deliberately describes what *we* do and do not
// charge for, and makes no factual claim about any competitor's prices --
// those change, and printing a rival's number that has gone stale is both
// wrong and actionable.
export const PRICING_HEADLINE = "Start free. Pay for the answers you use.";
export const PRICING_SUBHEAD =
  "Every plan includes a month of answers. A busy month costs a little more, up to a " +
  "spending limit you set. No seats, no annual contract, and every price is on this page.";

// The install line drawn in the hero's product shot.
//
// Split out and pinned by test (Home.test.tsx) against embedSnippet(), because
// this is a snippet a visitor can copy off a public page before they have an
// account. The filename has to be the one Hosting actually serves -- Vite never
// sees this string, so nothing else would catch it turning into a 404. See
// scripts/build.mjs, which copies widget/build-bots-now.js into dist/public.
export const DEMO_SCRIPT_URL = "https://buildbotsnow.com/build-bots-now.js";
export const DEMO_AGENT_ID = "ab12CD34";

// The real house bot in the corner of the public pages (components/SiteAssistant).
// Owned by the hello@buildbotsnow.com account, which is complimentary.
export const SITE_ASSISTANT_AGENT_ID = "OkdOjrU721b0n3JUOjSTzw";

// The hero carousel: everywhere a business can put its assistant, one picture
// each (client/public/hero/*.webp): bold modern editorial illustrations in
// teal, coral, mustard and cream, where the robot is the only purple, made
// with Gemini's image model from one robot reference so it is the same
// character throughout. The prompts, the robot reference and the style
// reference are in scripts/hero-images. The captions are HTML rather than part
// of the pictures so they follow dark mode and can be translated; `alt` says
// what each picture shows, including its speech bubble.
export type HeroSlide = { src: string; title: string; body: string; alt: string };

export const HERO_SLIDES: HeroSlide[] = [
  {
    src: "/hero/website.webp",
    title: "On your website",
    body: "Answers your customers while you enjoy your coffee.",
    alt: "A shop owner sips her coffee while the robot stands on the counter beside her laptop's online store and tells a customer: Yes! We ship to Canada."
  },
  {
    src: "/hero/business-card.webp",
    title: "On your business card",
    body: "A QR code that answers for you, even when you're under a sink.",
    alt: "A plumber fixes the pipes under a sink while the robot stands on his business card, next to its QR code, and tells a customer: Mike can be there by 3pm!"
  },
  {
    src: "/hero/profile.webp",
    title: "On LinkedIn and your profiles",
    body: "One link, and people get answers before they message you.",
    alt: "An accountant works at his desk; the robot stands beside his screen, which shows his profile with an Ask my assistant button, and says: Yes, he is taking new clients!"
  },
  {
    src: "/hero/email-signature.webp",
    title: "In your email signature",
    body: "Questions answered and appointments booked, straight from your emails.",
    alt: "A dentist holds a tablet showing an email while the robot beside her holds up a calendar card for Tuesday at 10:00 and says: Your cleaning is booked!"
  },
  {
    src: "/hero/shop-window.webp",
    title: "On your shop window",
    body: "Scan, ask, and get an answer, even after closing time.",
    alt: "A customer scans a QR code on a bakery door while the robot waves from the display counter in the window and says: Fresh sourdough at 7am!"
  }
];
